import { View, StyleSheet } from "react-native";
import DefaultLayout from "../layouts/DefaultLayout";
import CustomText from "../components/common/general/CustomText/CustomText.js";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import List from "../components/admin/List/List.tsx";
import Delete from "../components/svgs/common/Delete";
import CustomButton from "../components/common/general/CustomButton/CustomButton.js";
import CustomDropdown from "../components/common/general/CustomDropDown/CustomDropdown";
import { useNavigation } from "@react-navigation/native";
import {
  useGetAllTenantsQuery,
  useGetOAuthClientsQuery,
  GetOAuthClientsDocument,
  useCreateOAuthClientMutation,
  useDeleteOAuthClientMutation,
} from "@axio/graphql/generated/graphql";
import { useForm, Controller } from "react-hook-form";
import { useToast } from "../contexts/Toast/ToastProvider";

function OAuthClientDashboardView() {
  const navigation = useNavigation();
  const { data, loading, error } = useGetOAuthClientsQuery();
  const { data: tenantData } = useGetAllTenantsQuery();
  const [createClient] = useCreateOAuthClientMutation({
    refetchQueries: [{ query: GetOAuthClientsDocument }],
  });
  const [deleteClient] = useDeleteOAuthClientMutation({
    refetchQueries: [{ query: GetOAuthClientsDocument }],
  });
  const { showToast } = useToast();
  const tenantOptions = tenantData?.getAllTenants?.data.map((t) => ({ label: t.tenantDomain, value: t.id })) || [];

  const { control, handleSubmit, reset, watch } = useForm({ defaultValues: { tenantId: "" } });
  const selectedTenantId = watch("tenantId");

  const onSubmit = async (values) => {
    const result = await createClient({ variables: { input: values } });
    const created = result.data?.createOAuthClient?.data?.[0];
    if (created) {
      showToast({
        message: `Client ID: ${created.clientId}\n\nSecret: ${created.clientSecret}`,
        type: "info",
      });
    }
    reset();
  };

  const handleDelete = async (id) => {
    await deleteClient({ variables: { id } });
  };

  const handleGoBack = () => {
    navigation.navigate("Admin");
  };

  const clients = data?.getOAuthClients.data.map((client) => ({
    title: `Tenant: ${client.tenant?.tenantDomain}`,
    rowItems: [
      { text: `Id: ${client.clientId}`, copyValue: client.clientId },
      { text: `Secret: ${client.clientSecret}`, copyValue: client.clientSecret },
    ],
    icon: null,
    buttons: [{ icon: <Delete />, onPress: () => handleDelete(client.id) }],
  }));

  return (
    <DefaultLayout pageHasChat={false}>
      <CustomButton
        text="← Back to Dashboard"
        onPress={handleGoBack}
        useTranslationText={false}
        styleType="transparent"
        size="s"
        style={styles.backButton}
      />
      <CustomSpacing size="m" />
      <CustomText
        role="header"
        text="API Keys"
        size="l"
        textType="display"
        weight="ultraBold"
        useTranslationText={false}
      />
      <CustomSpacing size="m" />
      <View style={styles.formContainer}>
        <CustomText role="header" text="Create API Key" size="m" useTranslationText={false} />
        <CustomSpacing size="s" />
        <Controller
          control={control}
          name="tenantId"
          render={({ field: { onChange, value } }) => (
            <CustomDropdown
              options={tenantOptions}
              value={value}
              onChange={onChange}
              placeholder="Select tenant"
              dropdownStyle={styles.dropdown}
              dropdownContainerStyle={styles.dropdownList}
              accessibilityLabel="Tenant dropdown"
            />
          )}
        />
        <CustomSpacing size="s" />
        <CustomButton
          text="Create"
          styleType="lightGreen"
          style={selectedTenantId ? {} : styles.disabledButton}
          textStyle={selectedTenantId ? {} : styles.disabledButtonText}
          onPress={handleSubmit(onSubmit)}
          disabled={!selectedTenantId}
        />
      </View>
      <CustomSpacing size="large" />
      {!loading && !error && <List items={clients} />}
    </DefaultLayout>
  );
}

export default OAuthClientDashboardView;

const styles = StyleSheet.create({
  backButton: {
    alignSelf: "flex-start",
  },
  formContainer: {
    width: "100%",
    paddingBottom: 20,
  },
  dropdown: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
  },
  disabledButton: {
    backgroundColor: "#f0f0f0",
    borderColor: "#ccc",
  },
  disabledButtonText: {
    color: "#999",
  },
});
